import type { CarState, VehicleData, Vec2 } from "../types";
import { add, clip, dot, headingVector, scale, sub } from "./math";

export interface CollisionBody {
  car: CarState;
  vehicle: VehicleData;
}

export function collisionRadius(vehicle: VehicleData): number {
  return Math.max(vehicle.physics.car_width * 0.68, vehicle.physics.car_length * 0.37);
}

interface VehicleContact { normal: Vec2; overlap: number }

function vehicleContact(first: CollisionBody, second: CollisionBody): VehicleContact | null {
  const delta = sub(second.car.position, first.car.position);
  const firstForward = headingVector(first.car.heading);
  const secondForward = headingVector(second.car.heading);
  const firstLateral: Vec2 = [-firstForward[1], firstForward[0]];
  const secondLateral: Vec2 = [-secondForward[1], secondForward[0]];
  const firstHalfLength = first.vehicle.physics.car_length * 0.64;
  const firstHalfWidth = first.vehicle.physics.car_width * 0.68;
  const secondHalfLength = second.vehicle.physics.car_length * 0.64;
  const secondHalfWidth = second.vehicle.physics.car_width * 0.68;
  let best: VehicleContact | null = null;
  for (const axis of [firstForward, firstLateral, secondForward, secondLateral]) {
    const firstRadius = firstHalfLength * Math.abs(dot(axis, firstForward)) + firstHalfWidth * Math.abs(dot(axis, firstLateral));
    const secondRadius = secondHalfLength * Math.abs(dot(axis, secondForward)) + secondHalfWidth * Math.abs(dot(axis, secondLateral));
    const signedDistance = dot(delta, axis);
    const overlap = firstRadius + secondRadius - Math.abs(signedDistance);
    if (overlap <= 0) return null;
    if (!best || overlap < best.overlap) best = {
      normal: signedDistance < 0 ? scale(axis, -1) : axis,
      overlap,
    };
  }
  return best;
}

function projectionRadius(body: CollisionBody, axis: Vec2): number {
  const forward = headingVector(body.car.heading);
  const lateral: Vec2 = [-forward[1], forward[0]];
  return body.vehicle.physics.car_length * 0.64 * Math.abs(dot(axis, forward))
    + body.vehicle.physics.car_width * 0.68 * Math.abs(dot(axis, lateral));
}

function applyCollisionVelocity(body: CollisionBody, velocity: Vec2, shoveScale = 1): void {
  const forward = headingVector(body.car.heading);
  const lateral = forward[0] * velocity[1] - forward[1] * velocity[0];
  const massResponse = clip((1200 / body.vehicle.mass) ** 3, 0.24, 1.9);
  body.car.heading += clip(
    lateral / (125 / shoveScale) * massResponse,
    -0.78 * massResponse,
    0.78 * massResponse,
  );
  const signedSpeed = dot(velocity, headingVector(body.car.heading));
  body.car.speed = clip(signedSpeed, -body.vehicle.physics.max_reverse_speed, body.vehicle.physics.max_forward_speed);
}

export function resolveVehicleCollision(first: CollisionBody, second: CollisionBody, restitution = 0.46, shoveScale = 2): boolean {
  if (first.car.airborne || second.car.airborne || first.car.stuntSegmentId !== null || second.car.stuntSegmentId !== null || Math.abs(first.car.z - second.car.z) > 12) return false;
  const centerDelta = sub(second.car.position, first.car.position);
  const centerDistance = Math.hypot(...centerDelta);
  const contact = vehicleContact(first, second);
  const legacyOverlap = collisionRadius(first.vehicle) + collisionRadius(second.vehicle) - centerDistance;
  if (!contact && legacyOverlap <= 0) return false;
  const normal = contact?.normal ?? (centerDistance > 1e-9 ? scale(centerDelta, 1 / centerDistance) : headingVector(first.car.heading + Math.PI / 2));
  const impactNormal = centerDistance > 1e-9 ? scale(centerDelta, 1 / centerDistance) : normal;
  const separationOverlap = projectionRadius(first, impactNormal) + projectionRadius(second, impactNormal) - centerDistance;
  const inverseFirst = 1 / first.vehicle.mass;
  const inverseSecond = 1 / second.vehicle.mass;
  const inverseTotal = inverseFirst + inverseSecond;
  const overlap = Math.max(contact?.overlap ?? 0, separationOverlap, legacyOverlap) + 0.05;
  first.car.position = sub(first.car.position, scale(impactNormal, overlap * inverseFirst / inverseTotal));
  second.car.position = add(second.car.position, scale(impactNormal, overlap * inverseSecond / inverseTotal));
  const velocityFirst = scale(headingVector(first.car.heading), first.car.speed);
  const velocitySecond = scale(headingVector(second.car.heading), second.car.speed);
  const relativeNormal = dot(sub(velocitySecond, velocityFirst), impactNormal);
  if (relativeNormal < 0) {
    const impulse = -(1 + restitution) * relativeNormal / inverseTotal;
    applyCollisionVelocity(first, sub(velocityFirst, scale(impactNormal, impulse * inverseFirst)), shoveScale);
    applyCollisionVelocity(second, add(velocitySecond, scale(impactNormal, impulse * inverseSecond)), shoveScale);
    const lateralShove = Math.min(7.5, -relativeNormal * 0.032) * shoveScale;
    first.car.position = sub(first.car.position, scale(impactNormal, lateralShove * inverseFirst / inverseTotal));
    second.car.position = add(second.car.position, scale(impactNormal, lateralShove * inverseSecond / inverseTotal));
  }
  for (const body of [first, second]) {
    if (![...body.car.position, body.car.heading, body.car.speed].every(Number.isFinite)) throw new Error("Vehicle contact produced invalid physics state.");
  }
  return true;
}
